import AbstractAccountGroup from "./AbstractAccountGroup.js";
import AccountGroupSpecification from "../accountSpecification/AccountGroupSpecification.js";

/**
 * `accountGroup` is a group containing account group composites,
 * intervals and lists.
 *
 * @param {abstractAccountGroup[]} [spec.children] - Groups
 * contained by the receiver. Some of those can be other account
 * group composites to form a tree.
 */
export default class AccountGroup extends AbstractAccountGroup {
	constructor({ children = [] } = {}) {
		super(...arguments);
		this._children = children;
	}

	getChildren() {
		return this._children;
	}

	setChartOfAccounts(chartOfAccounts) {
		super.setChartOfAccounts(chartOfAccounts);

		this._children.forEach((child) =>
			child.setChartOfAccounts(chartOfAccounts),
		);
	}

	displayLabel() {
		const coa = this._chartOfAccounts;

		if (!coa) {
			return super.displayLabel();
		}

		if (coa.getMainTree() === this || coa.getSecondaryTree() === this) {
			return coa.getName();
		}

		return super.displayLabel(...arguments);
	}

	asSpecification(accountOperation) {
		return new AccountGroupSpecification({
			groupId: this.getId(),
			groupLabel: this.getName(),
			accountOperation,
		});
	}

	accept(visitor) {
		return visitor.visitAccountGroup(this);
	}

	findById(id) {
		let foundChild = super.findById(id);

		if (foundChild) {
			return foundChild;
		}

		for (const child of this.getChildren()) {
			foundChild = child.findById(id);

			if (foundChild) {
				return foundChild;
			}
		}

		return null;
	}

	canBeAddedTo(group) {
		return group.canAddGroup(this);
	}

	includesKnownGroup() {
		return this._children.some(
			(child) =>
				child.representsKnownGroup() || child.includesKnownGroup(),
		);
	}

	hasDirectChild(group) {
		return this._children.some((child) => child.getId() === group.getId());
	}

	canRemove(group) {
		return (
			this.hasDirectChild(group) &&
			!group.representsKnownGroup() &&
			!group.includesKnownGroup() &&
			!group.isAliased()
		);
	}

	canAddGroup(group) {
		if (group && group.getId() === this._id) {
			return false;
		}

		if (!this._children.length) {
			return true;
		}

		if (group) {
			const directChild = this._children.some(
				(child) => child.getId() === group.getId(),
			);
			if (directChild) {
				return false;
			}
		}

		return this._children.every((child) => child.isAccountGroup());
	}

	hasAccountGroupChildren() {
		return this.getChildren().some((child) => child.isAccountGroup());
	}

	hasAccounts() {
		return this._children.some((child) => child.hasAccounts());
	}

	hasAccount(account) {
		return this._children.some((child) => child.hasAccount(account));
	}

	canAddInterval() {
		return !this.hasAccountGroupChildren();
	}

	canAddAccountList() {
		return !this.hasAccountGroupChildren();
	}

	isAccountGroup() {
		return true;
	}

	removeChild(group) {
		this._children = this._children.filter(
			(child) => child.getId() !== group.getId(),
		);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.children = this._children;

		return properties;
	}

	static getType() {
		return "AccountGroup";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.children = context.factory.materializeAll(data.children, context);
	}
}
